(() => {
'use strict';

const ID_MAP = {
  "method": "[data-hfc-method]",
  "rawOpts": "[data-hfc-raw-options]",
  "rawSafety": "[data-hfc-raw-safety]",
  "rawCa": "[data-hfc-raw-calcium]",
  "species": "[data-hfc-species]",
  "stage": "[data-hfc-stage]",
  "idealRow": "[data-hfc-ideal-weight-row]",
  "idealWeight": "[data-hfc-ideal-weight]",
  "idealUnit": "[data-hfc-ideal-unit]",
  "sizeRow": "[data-hfc-size-row]",
  "size": "[data-hfc-size]",
  "weight": "[data-hfc-weight]",
  "unit": "[data-hfc-unit]",
  "activity": "[data-hfc-activity]",
  "advToggle": "[data-hfc-advanced-toggle]",
  "protein": "[data-hfc-protein]",
  "protein2": "[data-hfc-secondary-protein]",
  "organ": "[data-hfc-organ]",
  "carb": "[data-hfc-carb]",
  "veg": "[data-hfc-vegetables]",
  "fruit": "[data-hfc-fruit]",
  "topper": "[data-hfc-toppers]",
  "oil": "[data-hfc-oil]",
  "selNotes": "[data-hfc-selection-notes]",
  "calcBtn": "[data-hfc-calculate]",
  "results": "[data-hfc-results]",
  "recipeSummary": "[data-hfc-recipe-summary]",
  "kpis": "[data-hfc-kpis]",
  "mealsSel": "[data-hfc-meals]",
  "mealOut": "[data-hfc-meal-output]",
  "batchDays": "[data-hfc-batch-days]",
  "batchOut": "[data-hfc-batch-output]",
  "copyBtn": "button[data-hfc-copy]",
  "printBtn": "[data-hfc-print]",
  "shopLink": "[data-hfc-shopping-link]",
  "prepList": "[data-hfc-preparation]",
  "supplements": "[data-hfc-supplements]",
  "premixNote": "[data-hfc-premix-note]",
  "advCard": "[data-hfc-advanced-results]",
  "mainWarn": "[data-hfc-result-notices]"
};
const EXACT_SELECTORS = {
  "#recipeTable tbody": "[data-hfc-recipe-table]",
  "#nutrientTable tbody": "[data-hfc-nutrient-table]",
  "#supplements .supp .amt": "[data-hfc-supplements] .homemade-food-calculator__supplement-amount",
  "#species button": "[data-hfc-species] button",
  "#method button": "[data-hfc-method] button",
  "#rawCa button": "[data-hfc-raw-calcium] button",
  "#unit button": "[data-hfc-unit] button"
};

class SHomemadeFoodCalculator extends HTMLElement {
  connectedCallback() {
    if (this.__hfcInitialized) return;
    this.__hfcInitialized = true;

    const root = this;
    const nativeDocument = window.document;
    const document = {
      getElementById(id) {
        const selector = ID_MAP[id];
        return selector ? root.querySelector(selector) : null;
      },
      querySelector(selector) {
        const mapped = EXACT_SELECTORS[selector] || selector;
        return root.querySelector(mapped);
      },
      querySelectorAll(selector) {
        const mapped = EXACT_SELECTORS[selector] || selector;
        return root.querySelectorAll(mapped);
      },
      createElement: nativeDocument.createElement.bind(nativeDocument),
      body: nativeDocument.body
    };

    const configNode = root.querySelector('script[type="application/json"][data-hfc-copy]');
    let HFC_CONFIG = { labels: {}, validation: {}, preparation: {} };
    if (configNode) {
      try { HFC_CONFIG = JSON.parse(configNode.textContent || '{}'); }
      catch (error) { console.error('Homemade Food Calculator: invalid configuration JSON.', error); }
    }

    function cfgLabel(fallback, ...keys){
      for(const key of keys){
        const value = HFC_CONFIG.labels && HFC_CONFIG.labels[key];
        if(typeof value === "string" && value.trim()) return value;
      }
      return fallback;
    }

    function prepConfig(method){
      const prep = HFC_CONFIG.preparation || {};
      const value = prep[method];
      return Array.isArray(value) ? value : [];
    }

    /* ---------------- DATA ---------------- */
    // Per 100 g cooked/as-fed: kcal, protein g, fat g, calcium mg, phosphorus mg
    const FOODS = {
      protein: {
        chicken_breast:{n:"Chicken breast (no skin)",kcal:165,pro:31,fat:3.6,ca:15,p:228},
        chicken_thigh:{n:"Chicken thigh (no skin)",kcal:209,pro:26,fat:10.9,ca:10,p:200},
        turkey:{n:"Turkey (no skin)",kcal:189,pro:29,fat:7,ca:21,p:210},
        duck:{n:"Duck (no skin)",kcal:201,pro:23.5,fat:11,ca:12,p:203},
        beef_lean:{n:"Beef, lean cut (round/topside)",kcal:182,pro:30,fat:6,ca:8,p:210},
        beef:{n:"Beef mince, lean (90/10)",kcal:217,pro:26,fat:11.8,ca:18,p:200},
        beef_regular:{n:"Beef mince, regular (80/20)",kcal:254,pro:25,fat:17,ca:24,p:175},
        lamb:{n:"Lamb",kcal:258,pro:25,fat:17,ca:9,p:190},
        pork:{n:"Lean pork",kcal:242,pro:27,fat:14,ca:19,p:220},
        kangaroo:{n:"Kangaroo",kcal:135,pro:28,fat:2,ca:10,p:220},
        venison:{n:"Venison",kcal:158,pro:30,fat:3,ca:7,p:200},
        goat:{n:"Goat",kcal:143,pro:27,fat:3,ca:17,p:200},
        bison:{n:"Bison",kcal:143,pro:28,fat:2.4,ca:8,p:210},
        wildboar:{n:"Wild boar",kcal:160,pro:28,fat:5,ca:14,p:200},
        quail:{n:"Quail",kcal:227,pro:25,fat:14,ca:15,p:300},
        crocodile:{n:"Crocodile",kcal:120,pro:26,fat:2,ca:10,p:200},
        emu:{n:"Emu",kcal:150,pro:28,fat:4,ca:8,p:210},
        salmon:{n:"Salmon",kcal:206,pro:22,fat:12,ca:15,p:250,fish:true},
        sardines:{n:"Sardines (in water, bones in)",kcal:208,pro:25,fat:11,ca:380,p:490,fish:true,oily:true,canned:true},
        mackerel:{n:"Mackerel",kcal:262,pro:24,fat:18,ca:15,p:250,fish:true,oily:true},
        tuna:{n:"Tuna (in water)",kcal:116,pro:26,fat:1,ca:11,p:190,fish:true,mercury:true,canned:true},
        whitefish:{n:"White fish (cod)",kcal:105,pro:23,fat:1,ca:25,p:203,fish:true},
        rabbit:{n:"Rabbit",kcal:197,pro:29,fat:8,ca:21,p:240},
        tripe:{n:"Green tripe (raw)",kcal:96,pro:15,fat:7,ca:90,p:66,tripe:true,rawOnly:true},
        egg:{n:"Whole egg",kcal:155,pro:13,fat:11,ca:56,p:198}
      },
      topper: {
        // Food toppers — a small real portion, folded into the bowl and the balance
        cottage:{n:"Cottage cheese",kcal:98,pro:11,fat:4.3,ca:83,p:159,kind:"food",dairy:true},
        yoghurt:{n:"Plain yoghurt",kcal:61,pro:3.5,fat:3.3,ca:121,p:95,kind:"food",dairy:true},
        kefir:{n:"Kefir",kcal:41,pro:3.3,fat:1,ca:120,p:90,kind:"food",dairy:true},
        bone_broth:{n:"Bone broth",kcal:15,pro:2,fat:0.5,ca:10,p:10,kind:"food"},
        // Supplement toppers — dosed as directed, NOT part of the gram balance
        fish_oil:{n:"Fish oil",kind:"supp",why:"Omega-3 (EPA/DHA) for skin, coat, joints and heart. Selecting it ticks off the fish-oil line in the stack."},
        salmon_oil:{n:"Salmon oil",kind:"supp",why:"Omega-3 (EPA/DHA) like fish oil — skin, coat, joints and heart. Covers the omega-3 line in the stack."},
        cod_liver_oil:{n:"Cod liver oil",kind:"supp",why:"Omega-3 plus vitamin A and D. Potent — measure carefully to avoid vitamin A/D toxicity. Covers the omega-3 line."},
        green_mussel:{n:"Green-lipped mussel",kind:"supp",why:"Natural glucosamine, chondroitin and omega-3 — excellent joint support for large breeds and seniors."},
        glucosamine:{n:"Glucosamine + chondroitin",kind:"supp",why:"Cartilage and joint support for large/giant breeds, seniors and active dogs."},
        probiotic:{n:"Probiotic powder",kind:"supp",why:"Gut and stool health — handy when transitioning diets or after antibiotics."},
        kelp:{n:"Kelp (seaweed)",kind:"supp",why:"Natural iodine and trace minerals — can cover the iodine line in the stack. Measure it; iodine has a low ceiling."},
        brewers_yeast:{n:"Brewer's yeast",kind:"supp",why:"B-vitamins for coat and energy, and a mild natural flea deterrent."},
        psyllium:{n:"Psyllium husk",kind:"supp",why:"Soluble fibre to firm up loose stools or ease mild constipation. Always give with water."}
      },
      organ: {
        none:{n:"None",kcal:0,pro:0,fat:0,ca:0,p:0},
        chicken_liver:{n:"Chicken liver",kcal:172,pro:25,fat:6,ca:11,p:300,vitA:"mod"},
        beef_liver:{n:"Beef liver",kcal:175,pro:27,fat:5,ca:6,p:490,vitA:"high"},
        lamb_liver:{n:"Lamb liver",kcal:168,pro:26,fat:5,ca:8,p:360,vitA:"high"},
        pork_liver:{n:"Pork liver",kcal:165,pro:26,fat:4.4,ca:10,p:290,vitA:"high"},
        beef_kidney:{n:"Beef kidney",kcal:158,pro:27,fat:5,ca:18,p:280,vitA:"low"},
        lamb_kidney:{n:"Lamb kidney",kcal:112,pro:20,fat:3,ca:13,p:240,vitA:"low"},
        beef_heart:{n:"Beef heart (muscle)",kcal:165,pro:28,fat:5,ca:8,p:210,vitA:"low",muscle:true},
        chicken_heart:{n:"Chicken heart (muscle)",kcal:185,pro:26,fat:8,ca:12,p:200,vitA:"low",muscle:true}
      },
      carb: {
        none:{n:"None (low-carb)",kcal:0,pro:0,fat:0,ca:0,p:0},
        white_rice:{n:"White rice",kcal:130,pro:2.7,fat:0.3,ca:10,p:43},
        brown_rice:{n:"Brown rice",kcal:123,pro:2.7,fat:1,ca:3,p:83},
        sweet_potato:{n:"Sweet potato",kcal:90,pro:2,fat:0.1,ca:38,p:54},
        potato:{n:"Potato",kcal:87,pro:2,fat:0.1,ca:8,p:60},
        oats:{n:"Oats",kcal:71,pro:2.5,fat:1.5,ca:9,p:77},
        quinoa:{n:"Quinoa",kcal:120,pro:4.4,fat:1.9,ca:17,p:152},
        pumpkin:{n:"Pumpkin",kcal:20,pro:0.7,fat:0.1,ca:15,p:30},
        pasta:{n:"Pasta",kcal:131,pro:5,fat:1,ca:7,p:58}
      },
      veg: {
        carrot:{n:"Carrot",kcal:41,pro:0.9,fat:0.2,ca:33,p:35},
        green_beans:{n:"Green beans",kcal:31,pro:1.8,fat:0.2,ca:37,p:38},
        broccoli:{n:"Broccoli",kcal:34,pro:2.8,fat:0.4,ca:47,p:66},
        spinach:{n:"Spinach",kcal:23,pro:2.9,fat:0.4,ca:99,p:49},
        peas:{n:"Peas",kcal:81,pro:5,fat:0.4,ca:25,p:108},
        zucchini:{n:"Zucchini",kcal:17,pro:1.2,fat:0.3,ca:16,p:38},
        kale:{n:"Kale",kcal:35,pro:2.9,fat:0.5,ca:150,p:92},
        cucumber:{n:"Cucumber",kcal:15,pro:0.7,fat:0.1,ca:16,p:24},
        celery:{n:"Celery",kcal:16,pro:0.7,fat:0.2,ca:40,p:24},
        cauliflower:{n:"Cauliflower",kcal:25,pro:1.9,fat:0.3,ca:22,p:44,gassy:true},
        brussels:{n:"Brussels sprouts",kcal:43,pro:3.4,fat:0.3,ca:42,p:69,gassy:true},
        capsicum:{n:"Capsicum (red, no seeds)",kcal:31,pro:1,fat:0.3,ca:7,p:26},
        beetroot:{n:"Beetroot",kcal:43,pro:1.6,fat:0.2,ca:16,p:40},
        bokchoy:{n:"Bok choy",kcal:13,pro:1.5,fat:0.2,ca:105,p:37},
        asparagus:{n:"Asparagus",kcal:20,pro:2.2,fat:0.1,ca:24,p:52,cook:true},
        cabbage:{n:"Cabbage",kcal:25,pro:1.3,fat:0.1,ca:40,p:26,gassy:true,goitrogen:true},
        mushrooms:{n:"Mushrooms (store-bought)",kcal:28,pro:2.2,fat:0.5,ca:6,p:87,cook:true},
        corn:{n:"Corn (off the cob)",kcal:96,pro:3.4,fat:1.5,ca:3,p:77}
      },
      fruit: {
        blueberries:{n:"Blueberries",kcal:57,pro:0.7,fat:0.3,ca:6,p:12},
        apple:{n:"Apple (no seeds)",kcal:52,pro:0.3,fat:0.2,ca:6,p:11},
        banana:{n:"Banana",kcal:89,pro:1.1,fat:0.3,ca:5,p:22},
        watermelon:{n:"Watermelon (deseeded)",kcal:30,pro:0.6,fat:0.15,ca:7,p:11},
        strawberry:{n:"Strawberry",kcal:32,pro:0.7,fat:0.3,ca:16,p:24},
        blackberry:{n:"Blackberries",kcal:43,pro:1.4,fat:0.5,ca:29,p:22},
        cranberry:{n:"Cranberries",kcal:46,pro:0.4,fat:0.1,ca:8,p:13},
        mango:{n:"Mango (no pit)",kcal:60,pro:0.8,fat:0.4,ca:11,p:14},
        pineapple:{n:"Pineapple (no core/skin)",kcal:50,pro:0.5,fat:0.1,ca:13,p:8},
        raspberry:{n:"Raspberries",kcal:52,pro:1.2,fat:0.65,ca:25,p:29},
        pear:{n:"Pear (no seeds)",kcal:57,pro:0.4,fat:0.14,ca:9,p:12},
        rockmelon:{n:"Rockmelon (cantaloupe)",kcal:34,pro:0.8,fat:0.2,ca:9,p:15},
        papaya:{n:"Papaya (no seeds)",kcal:43,pro:0.5,fat:0.26,ca:20,p:10},
        peach:{n:"Peach (no pit)",kcal:39,pro:0.9,fat:0.25,ca:6,p:20,stone:true},
        apricot:{n:"Apricot (no pit)",kcal:48,pro:1.4,fat:0.4,ca:13,p:23,stone:true},
        kiwi:{n:"Kiwi (peeled)",kcal:61,pro:1.1,fat:0.5,ca:34,p:34},
        honeydew:{n:"Honeydew melon",kcal:36,pro:0.5,fat:0.14,ca:6,p:11},
        orange:{n:"Orange (peeled)",kcal:47,pro:0.9,fat:0.12,ca:40,p:14,citrus:true},
        plum:{n:"Plum (no pit)",kcal:46,pro:0.7,fat:0.28,ca:6,p:16,stone:true},
        cherry:{n:"Cherry (no pit)",kcal:63,pro:1.1,fat:0.2,ca:13,p:21,stone:true,cherry:true}
      },
      oil: {
        none:{n:"None",kcal:0,pro:0,fat:0,ca:0,p:0},
        olive:{n:"Olive oil",kcal:884,pro:0,fat:100,ca:1,p:0},
        canola:{n:"Canola oil",kcal:884,pro:0,fat:100,ca:0,p:0},
        flaxseed:{n:"Flaxseed oil",kcal:884,pro:0,fat:100,ca:1,p:0,ala:true},
        sunflower:{n:"Sunflower oil",kcal:884,pro:0,fat:100,ca:0,p:0,omega6:true},
        safflower:{n:"Safflower oil",kcal:884,pro:0,fat:100,ca:0,p:0,omega6:true},
        hemp:{n:"Hemp seed oil",kcal:884,pro:0,fat:100,ca:0,p:0,balanced:true},
        avocado:{n:"Avocado oil",kcal:884,pro:0,fat:100,ca:0,p:0},
        chicken_fat:{n:"Chicken fat",kcal:900,pro:0,fat:100,ca:0,p:0,animal:true},
        beef_tallow:{n:"Beef tallow",kcal:902,pro:0,fat:100,ca:0,p:0,animal:true},
        duck_fat:{n:"Duck fat",kcal:900,pro:0,fat:100,ca:0,p:0,animal:true},
        ghee:{n:"Ghee (clarified butter)",kcal:900,pro:0,fat:100,ca:0,p:0,animal:true},
        mct:{n:"MCT oil",kcal:830,pro:0,fat:100,ca:0,p:0,mct:true},
        coconut:{n:"Coconut oil",kcal:862,pro:0,fat:100,ca:1,p:0}
      }
    };

    // AAFCO & FEDIAF minimums per 1000 kcal ME
    const TARGET = {
      dog:{
        adult:{pro:45,fat:13.8,ca:1250,p:1000,caMax:6250,pMax:4000,vitE:12.5,fediaf_pro:45,fediaf_fat:16.9,fediaf_ca:1250,fediaf_p:1000,taurine:0},
        growth:{pro:56.3,fat:21.3,ca:3000,p:2500,caMax:6250,pMax:4000,vitE:12.5,fediaf_pro:62.5,fediaf_fat:21.3,fediaf_ca:3000,fediaf_p:2500,taurine:0}
      },
      cat:{
        adult:{pro:65,fat:22.5,ca:1500,p:1250,caMax:0,pMax:0,vitE:10,fediaf_pro:62.5,fediaf_fat:22.5,fediaf_ca:1000,fediaf_p:840,taurine:250},
        growth:{pro:75,fat:22.5,ca:2500,p:2000,caMax:0,pMax:0,vitE:10,fediaf_pro:70,fediaf_fat:22.5,fediaf_ca:2500,fediaf_p:1800,taurine:250}
      }
    };

    // Dog adult-size categories: growth duration + orthopedic risk
    const SIZE = {
      toy:   {n:"Toy / Small", months:"10–12", bigGrowth:false, joint:false, tiny:true},
      medium:{n:"Medium",      months:"12",    bigGrowth:false, joint:false, tiny:false},
      large: {n:"Large",       months:"15–18", bigGrowth:true,  joint:true,  tiny:false},
      giant: {n:"Giant",       months:"18–24", bigGrowth:true,  joint:true,  tiny:false}
    };

    // MER factors
    const ACT = {
      dog:[
        {v:1.6,l:"Neutered adult (typical)"},
        {v:1.8,l:"Intact adult"},
        {v:1.0,l:"Weight loss / overweight",wl:true},
        {v:1.4,l:"Prone to weight gain"},
        {v:2.5,l:"Active / working"},
        {v:2.0,l:"Growth: puppy > 4 months"},
        {v:3.0,l:"Growth: puppy < 4 months"},
        {v:3.0,l:"Pregnant (late) / nursing"}
      ],
      cat:[
        {v:1.2,l:"Neutered adult (typical)"},
        {v:1.4,l:"Intact adult"},
        {v:0.8,l:"Weight loss / overweight",wl:true},
        {v:1.0,l:"Prone to weight gain"},
        {v:1.6,l:"Active cat"},
        {v:2.5,l:"Growth: kitten"},
        {v:2.0,l:"Pregnant / nursing"}
      ]
    };

    /* Default recipe template (share of total food WEIGHT, before scaling to kcal) */
    const TEMPLATE = {
      cooked:{
        dog:  {protein:0.55, organ:0.05, carb:0.25, veg:0.09, fruit:0.04, topper:0.04, oil:0.02},
        cat:  {protein:0.75, organ:0.07, carb:0.07, veg:0.05, fruit:0.02, topper:0.03, oil:0.04}
      },
      raw:{
        dog:  {protein:0.72, organ:0.08, carb:0.05, veg:0.09, fruit:0.03, topper:0.04, oil:0.03},
        cat:  {protein:0.85, organ:0.09, carb:0.00, veg:0.03, fruit:0.00, topper:0.03, oil:0.03}
      }
    };

    /* Raw meats/organs are ~27% more moisture than cooked, so they are less
       calorie- and nutrient-dense per 100 g as-fed. This factor converts the
       cooked database values to raw as-fed values (proteins & organs only). */
    const RAW_FACTOR = 0.73;
    function F(cat,key){
      const f = FOODS[cat][key];
      // Raw moisture adjustment applies only to cooked-meat values — not to dairy,
      // green tripe or canned fish, whose stored values are already as-fed.
      if(state.method==="raw" && (cat==="protein"||cat==="organ") && !f.dairy && !f.tripe && !f.canned){
        return {n:f.n,kcal:f.kcal*RAW_FACTOR,pro:f.pro*RAW_FACTOR,fat:f.fat*RAW_FACTOR,
                ca:f.ca*RAW_FACTOR,p:f.p*RAW_FACTOR,vitA:f.vitA,muscle:f.muscle};
      }
      return f;
    }

    /* ---------------- STATE ---------------- */
    const state = {
      species:"dog", stage:"adult", unit:"kg", advanced:false,
      method:"cooked", rawCa:"bone", size:"medium",
      protein:"chicken_thigh", protein2:"none", organ:["beef_liver"], carb:"sweet_potato",
      veg:["carrot","green_beans"], fruit:["blueberries"], topper:[], oil:"none"
    };

    /* ---------------- UI BUILD ---------------- */
    function chipHTML(cat, key, f, selected, organStyle){
      const classes = ["homemade-food-calculator__chip"];
      if(selected) classes.push(organStyle ? "is-selected-organ" : "is-selected");
      return `<button type="button" class="${classes.join(" ")}" data-cat="${cat}" data-k="${key}" aria-pressed="${selected ? "true" : "false"}">${f.n}</button>`;
    }
    // Items flagged rawOnly (cooking is unsafe or destroys their value) only appear in raw mode
    function entriesFor(cat){
      return Object.entries(FOODS[cat]).filter(([k,f])=> !(f.rawOnly && state.method==="cooked"));
    }
    function renderChips(){
      const single = (cat,selKey,organStyle)=>entriesFor(cat)
          .map(([k,f])=>chipHTML(cat,k,f,k===selKey,organStyle)).join("");
      document.getElementById("protein").innerHTML = single("protein",state.protein,false);
      document.getElementById("protein2").innerHTML =
        chipHTML(
          "protein2",
          "none",
          { n: cfgLabel("None", "none") },
          state.protein2 === "none",
          false
        ) +
        entriesFor("protein").filter(([k])=>k!==state.protein)
          .map(([k,f])=>chipHTML("protein2",k,f,k===state.protein2,false)).join("");
      document.getElementById("organ").innerHTML = entriesFor("organ")
          .map(([k,f])=>chipHTML(
            "organ",
            k,
            f,
            k === "none" ? state.organ.length === 0 : state.organ.includes(k),
            true
          )).join("");
      document.getElementById("carb").innerHTML    = single("carb",state.carb,false);
      document.getElementById("oil").innerHTML     = single("oil",state.oil,false);
      document.getElementById("veg").innerHTML = entriesFor("veg")
          .map(([k,f])=>chipHTML("veg",k,f,state.veg.includes(k),false)).join("");
      document.getElementById("fruit").innerHTML = entriesFor("fruit")
          .map(([k,f])=>chipHTML("fruit",k,f,state.fruit.includes(k),false)).join("");
      document.getElementById("topper").innerHTML = entriesFor("topper")
          .map(([k,f])=>chipHTML("topper",k,f,state.topper.includes(k),false)).join("");
      updateNotes();
    }
    function renderActivity(){
      const sel=document.getElementById("activity");
      sel.innerHTML = ACT[state.species].map((a,i)=>`<option value="${a.v}"${a.wl?' data-wl="1"':''}>${a.l}</option>`).join("");
      // default based on stage
      if(state.stage==="growth"){
        const gi = ACT[state.species].findIndex(a=>/Growth/.test(a.l));
        sel.selectedIndex = gi>=0?gi:0;
      } else sel.selectedIndex = 0;
      toggleIdeal();
    }

    /* ---------------- SELECTION NOTES (live + in results) ---------------- */
    // Returns contextual guidance based on the CURRENT ingredient choices only
    // (no diet calc needed) so it can update the instant someone selects something.
    function buildIngredientNotes(){
      const n=[];
      if(state.stage==="growth") n.push({t:"warn",h:"Growth diet — highest risk",
        b:"Puppies and kittens are the easiest to get wrong. Have this formula checked by a veterinary nutritionist before feeding it as the sole diet."});
      // Dog size-specific guidance
      if(state.species==="dog"){
        const sz = SIZE[state.size];
        if(state.stage==="growth"){
          if(sz.bigGrowth) n.push({t:"crit",h:"Large/giant puppy — control calcium, don't maximise it",
            b:"This is the #1 home-feeding mistake for big pups. Their skeletons grow for "+sz.months+" months and excess calcium during that window causes permanent joint disease (OCD, HOD, hip &amp; elbow dysplasia). Keep Ca:P around 1.1–1.3:1, do NOT add calcium beyond the amount calculated here, feed for lean, steady growth (not maximal), and get this diet checked by a veterinary nutritionist."});
          n.push({t:"info",h:"Feed growth formula to ~"+sz.months+" months",
            b:"A "+sz.n.toLowerCase()+" dog stays on the growth recipe until roughly "+sz.months+" months, then switch to the adult maintenance setting. Switching too early starves growth; too late risks excess weight."});
        } else {
          if(sz.joint) n.push({t:"info",h:sz.n+" breed — support the joints",
            b:"Large and giant breeds carry more joint load. The fish oil in the stack helps; a glucosamine/chondroitin supplement and keeping them lean are the biggest levers for long-term mobility."});
          if(sz.tiny) n.push({t:"info",h:"Small breed — dense meals, small portions",
            b:"Toy and small dogs need calorie-dense food in small amounts and can be fussy. Very young toy pups are prone to hypoglycaemia, so feed them little and often."});
        }
      }
      if(state.species==="cat") n.push({t:"info",h:"Cats need taurine every day",
        b:"Cats are obligate carnivores. Taurine is added in the supplement stack because cooking destroys it — deficiency causes blindness and fatal heart disease (DCM)."});
      const selectedOrgans = (state.organ || [])
          .filter(k=>k !== "none" && FOODS.organ[k])
          .map(k=>({key:k, ...FOODS.organ[k]}));
      if(!selectedOrgans.length) n.push({t:"warn",h:"No organ selected",
        b:"Without liver, the recipe leans harder on the vitamin-mineral premix to cover vitamin A, copper and B-vitamins."});
      else {
        selectedOrgans.filter(org=>org.vitA==="high").forEach(org=>n.push({t:"warn",h:org.n+" is very high in vitamin A & copper",
          b:"Powerful, but don't exceed the total organ share calculated here. Chronic over-feeding of red-meat liver causes vitamin A toxicity (bone and joint disease)."}));
        selectedOrgans.filter(org=>org.muscle).forEach(org=>n.push({t:"info",h:org.n+" is muscle meat, not a secreting organ",
          b:"Safe in larger amounts than liver and an excellent natural taurine source. If heart is your only selected \"organ\", still add some liver or a premix for vitamin A and copper."}));
        if(!selectedOrgans.some(org=>org.key.includes("liver"))) n.push({t:"warn",h:"No liver selected",
          b:"Heart is muscle meat and kidney is a different secreting organ. Add some liver or rely on a complete vitamin-mineral premix for vitamin A and copper."});
      }
      const protKeysRaw = keys => [state.protein, state.protein2].some(k=>keys.includes(k));
      const protList = [state.protein, state.protein2]
          .filter(k=>k && k!=="none" && FOODS.protein[k])
          .map(k=>({key:k, ...FOODS.protein[k]}));
      const prot = { oily:protList.some(x=>x.oily), mercury:protList.some(x=>x.mercury),
                     tripe:protList.some(x=>x.tripe), fish:protList.some(x=>x.fish) };
      protList.filter(x=>x.oily).forEach(x=> n.push({t:"info",h:x.n+" is rich in omega-3s",
        b:"Oily fish naturally supplies EPA/DHA"+(x.key==="sardines"?", and the soft edible bones bring their own calcium and phosphorus — a natural bone-in food":"")+". You can reduce the fish-oil capsules in the stack when it's a regular part of the bowl."}));
      if(prot.mercury) n.push({t:"warn",h:"Tuna — feed occasionally only",
        b:"Tuna is a large, long-lived fish and accumulates mercury, and cats can become 'addicted' to it. Use it as an occasional protein, not a daily staple, and rotate with other proteins."});
      if((state.topper||[]).some(k=>FOODS.topper[k].dairy)) n.push({t:"info",h:"Dairy toppers — keep them small",
        b:"Cottage cheese, yoghurt and kefir add protein, calcium and (for yoghurt/kefir) probiotics, but they're toppers — kept to a small part of the bowl here. They're lower in lactose than milk, but cut back if you see loose stools."});
      if(prot.tripe) n.push({t:"info",h:"Green tripe — raw feeders' favourite",
        b:"Unwashed 'green' tripe brings natural probiotics, enzymes and a good Ca:P balance. It's lower in calories and protein than muscle meat, so pair it with a leaner meat rather than running it as the sole protein."});
      if(state.method==="raw"){
        if(protKeysRaw(["pork","wildboar"])) n.push({t:"crit",h:"Raw pork / wild boar — parasite risk",
          b:"Pork and especially wild boar can carry Trichinella and Aujeszky's disease (pseudorabies), which is fatal to dogs. If you feed these raw, deep-freeze for at least 3 weeks first — or better, cook them and keep raw for other proteins."});
        if(protKeysRaw(["egg"])) n.push({t:"warn",h:"Raw egg — a couple of cautions",
          b:"Raw egg white contains avidin, which blocks biotin if fed often, and raw egg carries a salmonella risk. Occasional raw egg is fine for most dogs; for regular feeding, lightly cook them."});
        if(prot.fish) n.push({t:"warn",h:"Raw fish carries a thiamine risk",
          b:"Raw fish contains thiaminase, an enzyme that destroys vitamin B1 (thiamine). Feed raw fish only occasionally, freeze it first for parasites, and make sure the recipe is topped up with thiamine (in your B-vitamin premix)."});
        const grains=["white_rice","brown_rice","oats","quinoa","pasta"];
        if(grains.includes(state.carb)) n.push({t:"info",h:"Grains still need cooking",
          b:"Even in a raw diet, grains like rice, oats, quinoa and pasta must be cooked to be digestible. For a truly raw bowl, swap to a raw-friendly carb such as pumpkin or lightly-steamed sweet potato, or choose None."});
      }
      if(state.oil==="flaxseed") n.push({t:"info",h:"Flaxseed oil is a plant omega-3 (ALA)",
        b:"Dogs convert ALA to active EPA/DHA poorly, and cats essentially can't. Keep it as a coat/fibre extra, not a replacement for the fish oil in the supplement stack."});
      if((state.topper||[]).some(k=>["fish_oil","salmon_oil","cod_liver_oil"].includes(k))) n.push({t:"info",h:"Omega-3 oils — don't cook them",
        b:"Stir fish, salmon or cod liver oil in raw after cooking and keep the bottle refrigerated — heat and air destroy the omega-3s. They cover the EPA/DHA line in the supplement stack."});
      if((state.topper||[]).includes("cod_liver_oil")) n.push({t:"warn",h:"Cod liver oil — mind the vitamin A &amp; D",
        b:"A great omega-3 source, but cod liver oil is very high in vitamins A and D, which accumulate and can become toxic. Measure it precisely, don't pair it with other vitamin-A supplements or lots of liver, and use plain fish or salmon oil if you only want the omega-3s."});
      if((state.topper||[]).includes("psyllium")) n.push({t:"info",h:"Psyllium — always give with water",
        b:"Psyllium husk needs plenty of moisture to work and to avoid a gut blockage. Mix it into wet food and make sure fresh water is available."});
      if(state.oil==="mct") n.push({t:"info",h:"Introduce MCT oil gradually",
        b:"A fast energy source that may support senior cognition, but start with a few drops and build up over a week — too much too soon causes loose stools."});
      if(state.fruit.includes("pineapple")) n.push({t:"info",h:"Pineapple: small amounts only",
        b:"Safe and usually loved, but it's high in natural sugar and acid, so keep it to a few small pieces. The old tip that it stops dogs eating poo has little evidence behind it — don't rely on it."});
      if(state.fruit.some(k=>(FOODS.fruit[k]||{}).stone)){
        const cherry = state.fruit.includes("cherry");
        n.push({t:"warn",h:"Stone fruit — remove the pit, always",
          b:"The flesh is safe, but the stone contains cyanide and is a choking and gut-blockage hazard. Remove it every time and feed only the flesh."+(cherry?" Cherries are the riskiest here — the pits are small and easily swallowed whole, so many vets skip cherries altogether.":"")});
      }
      if(state.fruit.includes("orange")) n.push({t:"info",h:"Orange — a little, peeled",
        b:"Citrus is safe in small amounts for dogs (vitamin C) but acidic and sugary; peel it and remove seeds. Most dogs aren't keen and cats gain nothing, so it's optional."});
      if(state.veg.some(k=>(FOODS.veg[k]||{}).gassy)) n.push({t:"info",h:"Cruciferous veg can cause gas",
        b:"Cauliflower, brussels sprouts and cabbage are nutritious but produce wind and bloating in larger amounts. Keep them a small part of the veg and lightly cook to make them easier to digest."});
      if(state.veg.includes("asparagus")) n.push({t:"info",h:"Cook the asparagus",
        b:"Raw asparagus is tough and hard to digest — steam or lightly cook it, and chop it small."});
      if(state.veg.includes("cabbage")) n.push({t:"info",h:"Cabbage — moderation",
        b:"Fine in small amounts, but large quantities of raw cabbage are mildly goitrogenic (can affect the thyroid). Cooking reduces that, so lightly cook it and don't overdo it."});
      if(state.veg.includes("mushrooms")) n.push({t:"warn",h:"Mushrooms — store-bought only, cooked",
        b:"Plain supermarket mushrooms (button, cremini, portobello) are safe cooked. Wild mushrooms are a different story — some cause fatal liver failure and no one can reliably tell them apart in a lawn. If your dog eats a wild mushroom, treat it as an emergency."});
      if(state.veg.includes("corn")) n.push({t:"warn",h:"Corn — off the cob, always",
        b:"Corn kernels are safe and digestible. The cob is not — swallowed cob chunks are one of the classic intestinal obstructions we see in emergency, and they usually need surgery. Never let a dog chew a cob, even a 'cleaned' one."});
      if(state.species==="cat" && state.fruit.length) n.push({t:"info",h:"Cats barely benefit from fruit",
        b:"Fine in tiny amounts as a treat, but an obligate carnivore gains little from it. You can safely leave it out."});
      return n;
    }
    function noteHTML(x){return `<div class="homemade-food-calculator__selection-note homemade-food-calculator__selection-note--${x.t==="crit"?"danger":x.t==="warn"?"warning":"info"}"><strong>${x.h}.</strong> ${x.b}</div>`;}
    function updateNotes(){
      const notes=buildIngredientNotes();
      const el=document.getElementById("selNotes");
      el.innerHTML = notes.length
        ? `<div class="homemade-food-calculator__notes-heading">Heads up for your selections</div>`+notes.map(noteHTML).join("")
        : "";
    }

    /* ---------------- ENGINE ---------------- */
    function getWeightKg(){
      let w = parseFloat(document.getElementById("weight").value)||0;
      return state.unit==="lb" ? w*0.453592 : w;
    }
    function getIdealKg(){
      let w = parseFloat(document.getElementById("idealWeight").value)||0;
      return state.unit==="lb" ? w*0.453592 : w;
    }
    function isWeightLoss(){
      const sel=document.getElementById("activity");
      const o=sel.options[sel.selectedIndex];
      return !!(o && o.dataset.wl==="1");
    }
    function toggleIdeal(){
      document.getElementById("idealRow").hidden = !isWeightLoss();
    }
    function computeMER(kg){
      const rer = 70*Math.pow(kg,0.75);
      const factor = parseFloat(document.getElementById("activity").value)||1.6;
      return {rer, mer: rer*factor, factor};
    }
    // Build recipe: returns {items:[{cat,key,name,grams,kcal,pro,fat,ca,p}], totals}
    function buildRecipe(mer){
      const tmpl = TEMPLATE[state.method][state.species];
      // energy density per gram for each chosen component (weighted by veg avg)
      const comp = [];
      const hasP2 = state.protein2!=="none" && state.protein2!==state.protein && FOODS.protein[state.protein2];
      if(hasP2){
        comp.push({cat:"protein",key:state.protein,share:tmpl.protein*0.6});
        comp.push({cat:"protein",key:state.protein2,share:tmpl.protein*0.4});
      } else {
        comp.push({cat:"protein",key:state.protein,share:tmpl.protein});
      }
      const organKeys = (state.organ || []).filter(k=>k !== "none" && FOODS.organ[k]);
      if(organKeys.length) organKeys.forEach(ok=>comp.push({cat:"organ",key:ok,share:tmpl.organ/organKeys.length}));
      if(state.carb!=="none")  comp.push({cat:"carb",key:state.carb,share:tmpl.carb});
      if(state.oil!=="none")   comp.push({cat:"oil",key:state.oil,share:tmpl.oil});
      const vegKeys = state.veg || [];
      if(vegKeys.length) vegKeys.forEach(vk=>comp.push({cat:"veg",key:vk,share:tmpl.veg/vegKeys.length}));
      const fruitKeys = state.fruit || [];
      if(fruitKeys.length) fruitKeys.forEach(fk=>comp.push({cat:"fruit",key:fk,share:tmpl.fruit/fruitKeys.length}));
      // Only FOOD toppers (dairy, broth) join the bowl; supplement toppers are dosed separately.
      const foodToppers = (state.topper||[]).filter(k=>FOODS.topper[k].kind==="food");
      if(foodToppers.length) foodToppers.forEach(dk=>comp.push({cat:"topper",key:dk,share:tmpl.topper/foodToppers.length}));

      // renormalise shares (in case organ/carb/oil = none)
      const shareSum = comp.reduce((s,c)=>s+c.share,0);
      comp.forEach(c=>c.share=c.share/shareSum);

      // kcal per gram of the mixed diet = sum(share_i * kcal_i/100)
      let kcalPerG = comp.reduce((s,c)=>{
        const f = F(c.cat,c.key);
        return s + c.share*(f.kcal/100);
      },0);
      const totalGrams = mer / kcalPerG;

      const items = comp.map(c=>{
        const f = F(c.cat,c.key);
        const grams = totalGrams*c.share;
        return {
          cat:c.cat, key:c.key, name:f.n,
          grams,
          kcal: grams*f.kcal/100,
          pro: grams*f.pro/100,
          fat: grams*f.fat/100,
          ca: grams*f.ca/100,
          p: grams*f.p/100
        };
      }).filter(i=>i.grams>=0.5);   // drop zero-share rows (e.g. a category with 0 template weight)
      const totals = items.reduce((t,i)=>({
        grams:t.grams+i.grams, kcal:t.kcal+i.kcal, pro:t.pro+i.pro,
        fat:t.fat+i.fat, ca:t.ca+i.ca, p:t.p+i.p
      }),{grams:0,kcal:0,pro:0,fat:0,ca:0,p:0});

      return {items, totals};
    }

    // Supplements needed
    function computeSupplements(recipe, mer, kg){
      const t = TARGET[state.species][state.stage];
      const totals = recipe.totals;

      // --- Calcium ---
      // Anchor Ca:P at ~1.3:1 (physiologically the priority). We never force calcium up to
      // the absolute minimum if that would push the ratio past 2:1 — an inverted/excess ratio
      // is dangerous, especially for growing large-breed pups. Absolute shortfalls of Ca or P
      // are instead reported honestly and covered by a bone-meal / mineral premix.
      const dietCa = totals.ca; // mg
      const dietP  = totals.p;  // mg
      const per1000k = 1000/totals.kcal;
      let calcium;
      if(state.method==="raw" && state.rawCa==="bone"){
        // Raw feeders balance calcium by making raw meaty bone ~10-15% of the diet, not by
        // solving a ratio (bone carries its own phosphorus, so an exact-ratio solve demands
        // absurd amounts of bone). We use 14% edible bone as the starting point. Bone displaces
        // some boneless meat, so the net phosphorus it adds is modest. Bone content varies a lot,
        // which is why we still recommend verifying calcium / using a premix.
        const bone_g  = 0.14 * totals.grams;               // grams raw meaty bone (aim 10-15%)
        const finalCa = dietCa + 15*bone_g;                // ~15 mg Ca per g edible raw bone
        const finalP  = dietP  + 4*bone_g;                 // net P added after displacing muscle
        calcium = {mode:"bone", rmb_g:bone_g, bonePct:14, finalCa, finalP, caP: finalP>0? finalCa/finalP : 0};
      } else {
        const targetCa   = dietP*1.3;                      // mg elemental Ca to reach 1.3:1
        const caToAdd    = Math.max(0, targetCa - dietCa); // mg elemental Ca
        calcium = {mode:"powder", caToAdd, caco3_mg:caToAdd/0.40, eggshell_g:caToAdd/380,
                   finalCa: dietCa+caToAdd, finalP: dietP, caP:(dietCa+caToAdd)/dietP};
      }
      const caShort = (calcium.finalCa*per1000k) < t.ca*0.95;
      const pShort  = (calcium.finalP *per1000k) < t.p *0.95;

      // --- Fish oil (EPA+DHA) ---
      // ~75 mg combined EPA+DHA per kg for skin/coat & anti-inflam; 1000 mg standard fish oil ≈ 300 mg EPA+DHA
      const epaDha = 75*kg;
      const fishOil_mg = epaDha/0.30;
      const fishCaps = Math.max(1, Math.round(fishOil_mg/1000));

      // --- Vitamin E (added because we add fat/PUFA) ---
      const vitEmin = t.vitE*(totals.kcal/1000); // IU/day
      const vitE = Math.max(vitEmin, kg*1.5);

      // --- Taurine (cats always; dogs optional for certain diets) ---
      const taurine = state.species==="cat" ? Math.max(250, 250*(totals.kcal/1000)) : 0;

      // --- Iodine ---
      const iodine = state.species==="cat" ? 0.20*(totals.kcal/1000) : 0.22*(totals.kcal/1000); // mg/day approx

      return {
        calcium,
        fishOil_mg, fishCaps, epaDha,
        vitE, taurine, iodine,
        finalCa: calcium.finalCa, finalP: calcium.finalP,
        caP: calcium.caP,
        caShort, pShort
      };
    }

    // Nutrient table per 1000 kcal (post-supplement)
    function nutrientRows(recipe, supp){
      const t = TARGET[state.species][state.stage];
      const totals = recipe.totals;
      const k = 1000/totals.kcal;
      const rows = [
        {n:"Crude protein (g)", val:totals.pro*k, aafco:t.pro, fediaf:t.fediaf_pro},
        {n:"Crude fat (g)", val:totals.fat*k, aafco:t.fat, fediaf:t.fediaf_fat},
        {n:"Calcium (mg)", val:supp.finalCa*k, aafco:t.ca, fediaf:t.fediaf_ca},
        {n:"Phosphorus (mg)", val:supp.finalP*k, aafco:t.p, fediaf:t.fediaf_p},
      ];
      if(state.species==="cat") rows.push({n:"Taurine (mg)*", val:supp.taurine*k, aafco:t.taurine, fediaf:t.taurine});
      return rows;
    }

    /* ---------------- RENDER RESULTS ---------------- */
    let LAST=null;
    function calculate(doScroll){
      const kgActual = getWeightKg();
      if(!(kgActual>0)){ if(doScroll) alert(HFC_CONFIG.validation?.weight || "Please enter a valid body weight."); return; }
      // Weight loss feeds for the TARGET weight, not the current one
      const wl = isWeightLoss();
      const kgIdeal = wl ? getIdealKg() : 0;
      const kg = (wl && kgIdeal>0) ? kgIdeal : kgActual;
      const {rer,mer,factor} = computeMER(kg);
      const recipe = buildRecipe(mer);
      const supp = computeSupplements(recipe, mer, kg);
      LAST = {kg,kgActual,wl,mer,recipe,supp};

      // summary
      const stageTxt = state.stage==="growth"?"growth":"adult maintenance";
      const who = (wl && kgIdeal>0 && kgIdeal<kgActual)
        ? `A <strong>${kgActual.toFixed(1)} kg ${state.species}</strong> fed for a <strong>target weight of ${kg.toFixed(1)} kg</strong>`
        : `A <strong>${kg.toFixed(1)} kg ${state.species}</strong> on ${stageTxt}`;
      document.getElementById("recipeSummary").innerHTML =
        `${who} needs about <strong>${Math.round(mer)} kcal/day</strong>. Here's a <strong>${state.method}</strong> recipe that hits it:`;

      // kpis
      document.getElementById("kpis").innerHTML = `
        <div class="homemade-food-calculator__kpi"><div class="homemade-food-calculator__kpi-value">${Math.round(mer)}</div><div class="homemade-food-calculator__kpi-label">${cfgLabel("kcal / day", "kcalPerDay", "kcal_per_day")}</div></div>
        <div class="homemade-food-calculator__kpi"><div class="homemade-food-calculator__kpi-value">${Math.round(recipe.totals.grams)} g</div><div class="homemade-food-calculator__kpi-label">${cfgLabel("total food / day", "totalFood", "total_food")}</div></div>
        <div class="homemade-food-calculator__kpi"><div class="homemade-food-calculator__kpi-value">${Math.round(recipe.totals.pro)} g</div><div class="homemade-food-calculator__kpi-label">${cfgLabel("protein / day", "protein", "proteinPerDay", "protein_per_day")}</div></div>
        <div class="homemade-food-calculator__kpi"><div class="homemade-food-calculator__kpi-value">${supp.caP.toFixed(2)}:1</div><div class="homemade-food-calculator__kpi-label">${cfgLabel("balanced Ca:P", "balancedCaP", "balanced_ca_p")}</div></div>`;

      // recipe table
      const tb = document.querySelector("#recipeTable tbody");
      tb.innerHTML = recipe.items.map(i=>`<tr>
          <td>${i.name} <span style="color:var(--hfc-muted);font-size:12px">(${labelCat(i.cat)})</span></td>
          <td class="is-number">${Math.round(i.grams)} g</td>
          <td class="is-number">${Math.round(i.kcal)}</td></tr>`).join("")
        + `<tr class="is-total"><td>${cfgLabel("Total food", "total", "totalFoodRow", "total_food_row")}</td><td class="is-number">${Math.round(recipe.totals.grams)} g</td><td class="is-number">${Math.round(recipe.totals.kcal)}</td></tr>`;

      batchUpdate(); mealUpdate(); renderPrep();

      // supplements
      const S=[];
      const cal = supp.calcium;
      if(cal.mode==="bone"){
        S.push(suppRow("🦴", `Raw meaty bone: ~${Math.round(cal.rmb_g)} g/day (~${cal.bonePct}% of the bowl)`,
          `e.g. chicken necks, wings or ground bone-in mince. This is your calcium — count it as part of the meat, so swap ~${Math.round(cal.rmb_g)} g of the boneless meat above for bone-in. Lands Ca:P near ${supp.caP.toFixed(2)}:1. Feed bone <strong>raw and size-appropriate</strong> — never cooked (splinters), and never a hard weight-bearing bone that can crack teeth. Bone calcium varies a lot between cuts, so verify with a mineral premix if you want AAFCO-exact.`));
      } else {
        const extra = state.method==="raw" ? "bone meal or calcium powder" : "calcium carbonate";
        S.push(suppRow("🦴", `${state.method==="raw"?"Bone meal / calcium":"Calcium carbonate"}: ${fmtMg(cal.caco3_mg)}`,
          `or ${cal.eggshell_g.toFixed(1)} g eggshell powder (${extra}). Non-negotiable — boneless meat diets are ~10× too low in calcium. Brings Ca:P to ${supp.caP.toFixed(2)}:1.`));
      }
      const selT = state.topper || [];
      const omega3sel = selT.filter(k=>["fish_oil","salmon_oil","cod_liver_oil"].includes(k));
      if(omega3sel.length){
        const names = omega3sel.map(k=>FOODS.topper[k].n).join(" + ");
        S.push(suppRow("✅", `Omega-3 — you're adding ${names}`,
           `Target ≈ ${Math.round(supp.epaDha)} mg EPA+DHA/day. This covers the omega-3 requirement.${selT.includes("cod_liver_oil")?" Cod liver oil is also high in vitamin A &amp; D, so measure it carefully.":""}`));
      } else {
        S.push(suppRow("🐟", `Fish oil (EPA+DHA): ~${supp.fishCaps} × 1000 mg cap`,
           `≈ ${Math.round(supp.epaDha)} mg EPA+DHA/day for skin, coat, joints and heart. Skip if main protein is oily fish already, or add fish/salmon oil from Toppers &amp; extras.`));
      }
      S.push(suppRow("💊", `Vitamin E: ~${Math.round(supp.vitE)} IU`,
         `Added fat and fish oil raise the vitamin E requirement. Prevents deficiency.`));
      if(state.species==="cat"){
        const why = state.method==="raw"
          ? `Essential for cats — deficiency causes blindness and fatal heart disease (DCM). Raw meat retains more taurine than cooked, but supplementing is still the safe call, especially with heart meat in the mix.`
          : `Essential for cats — deficiency causes blindness and fatal heart disease (DCM). Cooking destroys taurine, so it must be added.`;
        S.push(suppRow("❤️", `Taurine: ${Math.round(supp.taurine)} mg`, why));
      } else {
        S.push(suppRow("❤️", `Taurine: 500–1000 mg (optional)`,
          `Recommended for large/giant breeds and grain-free or legume-heavy recipes to protect against diet-associated DCM.`));
      }
      if(selT.includes("kelp")){
        S.push(suppRow("✅", `Iodine — covered by kelp (topper selected)`,
           `Target ≈ ${supp.iodine.toFixed(2)} mg/day. Measure the kelp to its label — iodine has a low ceiling, so don't overdo it.`));
      } else {
        S.push(suppRow("🧂", `Iodine: ~${supp.iodine.toFixed(2)} mg`,
           `From a pinch of iodised salt or a measured kelp supplement (in Toppers &amp; extras). Home diets are typically iodine-deficient.`));
      }
      // Optional supplement toppers the owner chose (joint, gut, coat, fibre)
      const extraSupps = selT.filter(k=>FOODS.topper[k].kind==="supp" && !["fish_oil","salmon_oil","cod_liver_oil","kelp"].includes(k));
      extraSupps.forEach(k=>{
        const f = FOODS.topper[k];
        S.push(suppRow("➕", `${f.n} — as directed`, `${f.why} Follow the product's dosing for your pet's weight.`));
      });
      document.getElementById("supplements").innerHTML = S.join("");

      document.getElementById("premixNote").innerHTML =
        `<strong>Read this before you cook long-term:</strong> calcium, taurine and the omega-3s above are the big, calculable gaps — but a truly complete diet also needs guaranteed zinc, copper, manganese, iron, selenium, vitamin D, and B-vitamins. The safest way to cover all of them at once is a <strong>complete canine/feline vitamin-mineral premix</strong> (e.g. a balancer powder) dosed per its label for ${Math.round(recipe.totals.kcal)} kcal. Use the calcium figure above only if your premix does <em>not</em> already include calcium.`;

      // nutrient table
      const rows = nutrientRows(recipe, supp);
      document.querySelector("#nutrientTable tbody").innerHTML = rows.map(r=>{
        const status = statusPill(r.val, Math.max(r.aafco||0, r.fediaf||0));
        return `<tr>
          <td>${r.n}</td>
          <td class="is-number">${fmtN(r.val)}</td>
          <td class="is-number">${r.aafco?fmtN(r.aafco):"—"}</td>
          <td class="is-number">${r.fediaf?fmtN(r.fediaf):"—"}</td>
          <td>${status}</td></tr>`;
      }).join("");

      // main warning — same selection notes shown live, plus diet-computed ones
      const notes = buildIngredientNotes();
      if(supp.pShort) notes.push({t:"crit",h:"Phosphorus is below the profile minimum",
        b:"for this ingredient mix. Calcium powder can't fix this — add a measured bone meal or a complete mineral premix (which supplies both calcium and phosphorus) instead of plain calcium carbonate."});
      if(supp.caShort) notes.push({t:"crit",h:"Absolute calcium is still below the minimum",
        b:"even after topping up to a safe ratio. Increase the calcium/bone source per your premix label."});
      const fatPer1000 = recipe.totals.fat*(1000/recipe.totals.kcal);
      if(state.species==="dog" && fatPer1000>60) notes.push({t:"warn",h:"This is a high-fat recipe (~"+Math.round(fatPer1000)+" g fat/1000 kcal)",
        b:"Fine for most healthy, active dogs, but risky for pancreatitis-prone dogs (schnauzers, overweight or middle-aged dogs, previous episodes). Choose leaner cuts or skinless poultry to bring it down."});
      if(wl) notes.push({t:"warn",h:"Weight loss — done right",
        b:(kgIdeal>0 ? "This recipe feeds for the target weight of "+kg.toFixed(1)+" kg, not the current "+kgActual.toFixed(1)+" kg. " : "Enter a target (ideal) weight above so the recipe feeds for the goal, not the current weight. ")+"Aim for 1–2% of body weight lost per week, weigh every 2 weeks, and if nothing's moving after a month, see your vet — thyroid and other conditions can stall weight loss."});
      notes.push({t:"info",h:"Fresh water",
        b:"Roughly "+Math.round(kgActual*50)+"–"+Math.round(kgActual*60)+" ml/day for a "+kgActual.toFixed(1)+" kg "+state.species+", more in hot weather."+(state.species==="cat"?" Cats are chronic under-drinkers, and low water intake drives urinary disease — homemade food helps because it's moisture-rich, and a water fountain often gets cats drinking more.":"")});
      notes.push({t:"info",h:"Treats count too",
        b:"This recipe covers the full "+Math.round(mer)+" kcal/day. Keep treats under ~"+Math.round(mer*0.1)+" kcal/day (the 10% rule) or trim the recipe to match, otherwise weight creeps on."});
      notes.push({t:"info",h:"Transition and monitor",
        b:"Introduce any new diet over 7–10 days, and re-check body weight and condition every 2 weeks. Log it in ASK A VET™."});
      document.getElementById("mainWarn").innerHTML =
        `<div class="homemade-food-calculator__notes-heading" style="margin-bottom:9px">Before you feed this</div>`+notes.map(noteHTML).join("");

      document.getElementById("results").hidden=false;
      document.getElementById("advCard").hidden = !state.advanced;
      if(doScroll) document.getElementById("results").scrollIntoView({behavior:"smooth",block:"start"});
    }
    // Refresh results in place (no scroll) once they've been shown
    function recalc(){ if(LAST) calculate(false); }

    function renderPrep(){
      const fallbackCooked = [
        ["🍳","Cook it plain","Boil, bake or pan-cook the meat thoroughly with no salt, seasoning, onion, garlic or stock cubes. Cook carbs fully and lightly steam the vegetables."],
        ["🥣","Mix when cool","Let everything cool, chop small, and mix evenly so the bowl cannot be cherry-picked."],
        ["🧴","Supplements go in last","Stir calcium through cooled food. Add oils, probiotics and vitamins cold at serving."],
        ["🧊","Portion and store","Refrigerate for up to 3 days. Freeze the remainder in daily portions and thaw in the fridge."]
      ];
      const fallbackRaw = [
        ["🧊","Freeze first","Freeze and thaw meat safely in the refrigerator, especially fish, pork and wild game."],
        ["🔪","Keep it separate","Use separate utensils and wash hands, bowls and surfaces after every meal."],
        ["🦴","Bone rules","Feed bone raw only, size-appropriate and supervised. Never feed cooked bone."],
        ["⏱","Serve and clear","Remove uneaten raw food promptly. Refrigerate prepared portions and freeze the remainder."]
      ];
      const configured = prepConfig(state.method);
      const fallback = state.method === "raw" ? fallbackRaw : fallbackCooked;
      const steps = configured.length
        ? configured.map((step, index) => Array.isArray(step)
            ? [step[0] || fallback[index]?.[0] || "•", step[1] || "", step[2] || ""]
            : [step.icon || fallback[index]?.[0] || "•", step.title || "", step.text || step.body || ""])
        : fallback;
      const transitionTitle = (HFC_CONFIG.preparation && HFC_CONFIG.preparation.transitionTitle) || cfgLabel("The 7-day switch", "transitionTitle", "transition_title");
      const transitionHint = (HFC_CONFIG.preparation && HFC_CONFIG.preparation.transitionHint) || cfgLabel("Sensitive stomach? Stretch it to 10–14 days. Loose stools = hold at the previous step for two days before moving on.", "transitionHint", "transition_hint");
      const transition = `
        <div style="margin-top:16px">
          <div class="homemade-food-calculator__notes-heading" style="margin-bottom:6px">${transitionTitle}</div>
          <table>
            <thead><tr><th>${cfgLabel("Days", "days")}</th><th class="is-number">${cfgLabel("New food", "newFood", "new_food")}</th><th class="is-number">${cfgLabel("Old food", "oldFood", "old_food")}</th></tr></thead>
            <tbody>
              <tr><td>1–2</td><td class="is-number">25%</td><td class="is-number">75%</td></tr>
              <tr><td>3–4</td><td class="is-number">50%</td><td class="is-number">50%</td></tr>
              <tr><td>5–6</td><td class="is-number">75%</td><td class="is-number">25%</td></tr>
              <tr><td>7+</td><td class="is-number">100%</td><td class="is-number">—</td></tr>
            </tbody>
          </table>
          <div class="homemade-food-calculator__legend">${transitionHint}</div>
        </div>`;
      document.getElementById("prepList").innerHTML = steps.map(s=>
        `<div class="homemade-food-calculator__supplement-item"><div class="homemade-food-calculator__item-icon">${s[0]}</div><div><div class="homemade-food-calculator__item-title">${s[1]}</div><div class="homemade-food-calculator__item-text">${s[2]}</div></div></div>`).join("") + transition;
    }
    function mealUpdate(){
      if(!LAST) return;
      const m = parseInt(document.getElementById("mealsSel").value)||2;
      document.getElementById("mealOut").textContent =
        `${Math.round(LAST.recipe.totals.grams/m)} g ${cfgLabel("per meal", "perMeal", "per_meal")} (${m}${cfgLabel("× a day", "timesPerDay", "times_per_day")})`;
    }
    function batchUpdate(){
      if(!LAST) return;
      const d = Math.max(1, parseInt(document.getElementById("batchDays").value)||1);
      const out = LAST.recipe.items.map(i=>`${i.name} ${Math.round(i.grams*d)} g`).join(" · ");
      document.getElementById("batchOut").textContent = `${Math.round(LAST.recipe.totals.grams*d)} g ${cfgLabel("total", "totalBatch", "total_batch")}  (${out})`;
    }

    /* helpers */
    function labelCat(category){
      const labels = {
        protein: cfgLabel("protein", "ingredientCategoryProtein"),
        organ: cfgLabel("organ", "ingredientCategoryOrgan"),
        carb: cfgLabel("carb", "ingredientCategoryCarb"),
        veg: cfgLabel("veg", "ingredientCategoryVegetable"),
        fruit: cfgLabel("fruit", "ingredientCategoryFruit"),
        topper: cfgLabel("topper", "ingredientCategoryTopper"),
        oil: cfgLabel("oil", "ingredientCategoryOil")
      };

      return labels[category] || category;
    }
    function suppRow(ic,amt,why){return `<div class="homemade-food-calculator__supplement-item"><div class="homemade-food-calculator__item-icon">${ic}</div><div><div class="homemade-food-calculator__item-title homemade-food-calculator__supplement-amount">${amt}</div><div class="homemade-food-calculator__item-text">${why}</div></div></div>`;}
    function fmtMg(mg){return mg>=1000?(mg/1000).toFixed(1)+" g":Math.round(mg)+" mg";}
    function fmtN(v){return v>=100?Math.round(v):(Math.round(v*10)/10);}
    function statusPill(val,minReq){
      const meets = cfgLabel("meets", "meets");
      const close = cfgLabel("close", "close");
      const low = cfgLabel("low", "low");
      if(!minReq || val>=minReq) return `<span class="homemade-food-calculator__status homemade-food-calculator__status--meets">${meets}</span>`;
      if(val>=minReq*0.9) return `<span class="homemade-food-calculator__status homemade-food-calculator__status--close">${close}</span>`;
      return `<span class="homemade-food-calculator__status homemade-food-calculator__status--low">${low}</span>`;
    }

    /* ---------------- EVENTS ---------------- */
    document.getElementById("species").addEventListener("click",e=>{
      const b=e.target.closest("button"); if(!b)return;
      state.species=b.dataset.value;
      setActiveSegmentButton(document.getElementById("species"), b);
      // sensible defaults per species
      if(state.species==="cat"){state.protein="chicken_thigh";state.carb="none";state.oil="none";state.fruit=[];}
      else {state.protein="chicken_thigh";state.carb="sweet_potato";state.fruit=["blueberries"];}
      state.protein2="none";
      updateSizeVisibility(); renderActivity(); renderChips(); recalc();
    });
    function updateSizeVisibility(){
      document.getElementById("sizeRow").hidden = state.species!=="dog";
    }
    document.getElementById("size").addEventListener("change",e=>{
      state.size=e.target.value; updateNotes(); recalc();
    });
    function sanitizeRawOnly(){
      if(state.method!=="cooked") return;
      ["protein","carb","oil"].forEach(cat=>{
        const f=FOODS[cat][state[cat]];
        if(f && f.rawOnly) state[cat] = Object.keys(FOODS[cat]).find(k=>!FOODS[cat][k].rawOnly);
      });
      state.organ = (state.organ || []).filter(k=>{
        const f = FOODS.organ[k];
        return f && !f.rawOnly && k !== "none";
      });
      if(state.protein2!=="none" && (FOODS.protein[state.protein2]||{}).rawOnly) state.protein2="none";
      if(state.protein2===state.protein) state.protein2="none";  // main's fallback may collide with secondary
      ["veg","fruit","topper"].forEach(cat=>{
        state[cat] = (state[cat]||[]).filter(k=>!FOODS[cat][k].rawOnly);
      });
    }
    document.getElementById("method").addEventListener("click",e=>{
      const b=e.target.closest("button"); if(!b)return;
      state.method=b.dataset.value;
      setActiveSegmentButton(document.getElementById("method"), b);
      const raw = state.method==="raw";
      document.getElementById("rawOpts").hidden = !raw;
      document.getElementById("rawSafety").hidden = !raw;
      sanitizeRawOnly();             // drop any raw-only picks when switching to cooked
      renderChips();                 // re-render so raw-only items appear/disappear
      recalc();                      // amounts change with method, so refresh live
    });
    document.getElementById("rawCa").addEventListener("click",e=>{
      const b=e.target.closest("button"); if(!b)return;
      state.rawCa=b.dataset.value;
      setActiveSegmentButton(document.getElementById("rawCa"), b);
      updateNotes();
      recalc();
    });
    document.getElementById("stage").addEventListener("change",e=>{state.stage=e.target.value;renderActivity();updateNotes();recalc();});
    document.getElementById("weight").addEventListener("input",recalc);
    document.getElementById("idealWeight").addEventListener("input",recalc);
    document.getElementById("activity").addEventListener("change",()=>{toggleIdeal();recalc();});
    document.getElementById("unit").addEventListener("click",e=>{
      const b=e.target.closest("button"); if(!b)return;
      const cur=getWeightKg(), curIdeal=getIdealKg();
      state.unit=b.dataset.value;
      setActiveSegmentButton(document.getElementById("unit"), b);
      const inp=document.getElementById("weight");
      inp.value = state.unit==="lb" ? (cur/0.453592).toFixed(1) : cur.toFixed(1);
      const ideal=document.getElementById("idealWeight");
      if(curIdeal>0) ideal.value = state.unit==="lb" ? (curIdeal/0.453592).toFixed(1) : curIdeal.toFixed(1);
      document.getElementById("idealUnit").textContent = state.unit;
      recalc();
    });
    document.getElementById("advToggle").addEventListener("change",e=>{
      state.advanced=e.target.checked;
      document.getElementById("advCard").hidden = !(state.advanced && LAST);
    });
    ["protein","carb","oil"].forEach(cat=>{
      document.getElementById(cat).addEventListener("click",e=>{
        const b=e.target.closest("button"); if(!b)return;
        state[cat]=b.dataset.k;
        if(cat==="protein" && state.protein2===state.protein) state.protein2="none";
        renderChips(); recalc();
      });
    });
    document.getElementById("protein2").addEventListener("click",e=>{
      const b=e.target.closest("button"); if(!b)return;
      state.protein2 = b.dataset.k===state.protein2 ? "none" : b.dataset.k;  // click again to deselect
      renderChips(); recalc();
    });
    document.getElementById("organ").addEventListener("click",e=>{
      const b=e.target.closest("button"); if(!b)return;
      const k=b.dataset.k;
      if(k==="none") state.organ=[];
      else if(state.organ.includes(k)) state.organ=state.organ.filter(x=>x!==k);
      else state.organ.push(k);
      renderChips(); recalc();
    });
    ["veg","fruit","topper"].forEach(cat=>{
      document.getElementById(cat).addEventListener("click",e=>{
        const b=e.target.closest("button"); if(!b)return;
        const k=b.dataset.k;
        if(state[cat].includes(k)) state[cat]=state[cat].filter(x=>x!==k);
        else state[cat].push(k);
        renderChips(); recalc();
      });
    });
    document.getElementById("calcBtn").addEventListener("click",()=>calculate(true));
    document.getElementById("batchDays").addEventListener("input",batchUpdate);
    document.getElementById("mealsSel").addEventListener("change",mealUpdate);
    document.getElementById("printBtn").addEventListener("click",()=>window.print());

    function buildRecipeText(){
      if(!LAST) return "";
      const m = parseInt(document.getElementById("mealsSel").value)||2;
      const stageTxt = state.stage==="growth"?"growth":"adult maintenance";
      const L = [];
      L.push("ASK A VET™ — HOMEMADE FOOD CALCULATOR");
      L.push("By Dr Duncan Houston");
      L.push("");
      const forWho = (LAST.wl && LAST.kg<LAST.kgActual)
        ? `${LAST.kgActual.toFixed(1)} kg ${state.species}, fed for target weight ${LAST.kg.toFixed(1)} kg`
        : `${LAST.kg.toFixed(1)} kg ${state.species}`;
      L.push(`RECIPE FOR: ${forWho} (${state.method}, ${stageTxt})`);
      L.push(`Daily energy: ${Math.round(LAST.mer)} kcal | Total food: ${Math.round(LAST.recipe.totals.grams)} g/day (${Math.round(LAST.recipe.totals.grams/m)} g x ${m} meals)`);
      L.push("");
      L.push("INGREDIENTS (per day)");
      LAST.recipe.items.forEach(i=> L.push(`- ${i.name}: ${Math.round(i.grams)} g`));
      L.push("");
      L.push("DAILY SUPPLEMENTS");
      document.querySelectorAll("#supplements .supp .amt").forEach(e=> L.push("- "+e.textContent.trim()));
      L.push("");
      L.push("NOTES");
      L.push(`- Fresh water: roughly ${Math.round(LAST.kgActual*50)}-${Math.round(LAST.kgActual*60)} ml/day, more in heat.`);
      L.push("- Transition over 7 days: 25% new (days 1-2), 50% (3-4), 75% (5-6), 100% (day 7).");
      L.push("- Complete & balanced to AAFCO/FEDIAF profiles only WITH the supplements listed.");
      L.push("- Transition over 7-10 days. Healthy pets only - medical conditions need a vet-formulated diet.");
      if(state.method==="raw") L.push("- Raw feeding: freeze meat 3 weeks first, strict hygiene, never cooked bone.");
      L.push("");
      L.push("Made with the free ASK A VET™ Homemade Food Calculator");
      return L.join("\n");
    }
    document.getElementById("copyBtn").addEventListener("click",async()=>{
      const txt = buildRecipeText();
      if(!txt){ alert(HFC_CONFIG.validation?.copy || "Balance a recipe first, then copy it."); return; }
      let ok = false;
      try{ await navigator.clipboard.writeText(txt); ok = true; }
      catch(e){
        // fallback for restricted contexts
        const ta = document.createElement("textarea");
        ta.value = txt; ta.style.position="fixed"; ta.style.opacity="0";
        document.body.appendChild(ta); ta.select();
        try{ ok = document.execCommand("copy"); }catch(_){}
        document.body.removeChild(ta);
      }
      const btn = document.getElementById("copyBtn");
      const originalLabel = btn.dataset.originalLabel || btn.textContent;
      btn.dataset.originalLabel = originalLabel;
      btn.textContent = ok ? (HFC_CONFIG.validation?.copied || "✓ Copied — paste anywhere") : (HFC_CONFIG.validation?.copyFailed || "Couldn't copy — select & copy manually");
      setTimeout(()=>{ btn.textContent = originalLabel; }, 2500);
    });

    // Pass the finished recipe to the Shopping List & Meal Prep Calculator.
    // The destination calculator can read the recipe from location.hash.
    const shopLink = document.getElementById("shopLink");
    if(shopLink){
      shopLink.addEventListener("click", event=>{
        if(!LAST) return;

        const link = event.currentTarget;
        const baseUrl = link.href.split("#")[0];
        link.href = `${baseUrl}#r=${encodeURIComponent(buildRecipeText())}`;
      });
    }


    /* ---------------- SHOPIFY DEFAULTS ---------------- */
    state.method = root.dataset.defaultMethod || state.method;
    state.species = root.dataset.defaultSpecies || state.species;
    state.stage = root.dataset.defaultStage || state.stage;
    state.size = root.dataset.defaultSize || state.size;
    state.unit = root.dataset.defaultUnit || state.unit;

    if(state.species === "cat"){
      state.protein = "chicken_thigh";
      state.protein2 = "none";
      state.organ = ["chicken_liver"];
      state.carb = "none";
      state.veg = ["zucchini"];
      state.fruit = [];
      state.oil = "none";
    } else {
      state.protein = "chicken_thigh";
      state.protein2 = "none";
      state.organ = ["beef_liver"];
      state.carb = "sweet_potato";
      state.veg = ["carrot", "green_beans"];
      state.fruit = ["blueberries"];
      state.oil = "none";
    }

    const copyButton = document.getElementById("copyBtn");
    if(copyButton) copyButton.dataset.originalLabel = copyButton.textContent.trim();

    document.getElementById("weight").value = root.dataset.defaultWeight || document.getElementById("weight").value || 10;
    document.getElementById("stage").value = state.stage;
    document.getElementById("size").value = state.size;
    document.getElementById("mealsSel").value = root.dataset.defaultMeals || document.getElementById("mealsSel").value || 2;
    document.getElementById("batchDays").value = root.dataset.defaultBatchDays || document.getElementById("batchDays").value || 3;
    document.getElementById("idealUnit").textContent = state.unit;

    function setSegment(container, value){
      if(!container) return;
      container.querySelectorAll("button[data-value]").forEach(button=>{
        const active = button.dataset.value === value;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", active ? "true" : "false");
      });
    }

    function setActiveSegmentButton(container, activeButton){
      if(!container || !activeButton) return;

      container.querySelectorAll("button[data-value]").forEach(button=>{
        const active = button === activeButton;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", active ? "true" : "false");
      });
    }
    setSegment(document.getElementById("method"), state.method);
    setSegment(document.getElementById("species"), state.species);
    setSegment(document.getElementById("unit"), state.unit);
    setSegment(document.getElementById("rawCa"), state.rawCa);

    /* ---------------- INIT ---------------- */
    updateSizeVisibility();
    document.getElementById("rawOpts").hidden = state.method !== "raw";
    document.getElementById("rawSafety").hidden = state.method !== "raw";
    renderActivity(); renderChips();
  }
}

if (!customElements.get('s-homemade-food-calculator')) {
  customElements.define('s-homemade-food-calculator', SHomemadeFoodCalculator);
}
})();
